import * as React from "react";
import { MouseEvent, useEffect, useMemo, useState } from "react";
import { useSelector, useStore } from "react-redux";

import NumberFormat from "react-number-format";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { Slide, ToggleButton, ToggleButtonGroup } from "@mui/material";

import { setTotals } from "../Store/roiStore.ts";
import { SAMEDAY_SPEND_KEYS } from "./SameDaySpendDetails.tsx";
import { SAMEDAY_INVESTMENT_KEYS } from "./SameDayInvestment.tsx";

import "../styles/pages/roi-calculations.scss";
import "../styles/pages/sameday.scss";

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

// Per Thad/Jesse's formula, the constant savings/improvement multipliers are:
//   - Accuracy (combined): 20% capture of uncontrolled spend × pricing inaccuracy,
//                          where both inputs are the simple average of the no-ERP and
//                          off-contract values to avoid overstating via double-count
//   - Charge capture:       5% capture of un-captured spend (Thad's prose said "0.5%"
//                            but his example math only resolves to $250k at 5%; flagged
//                            with Thad to confirm)
//   - Processing labor:     90% reduction of bill-only labor cost
//   - Contract price:       5% pricing improvement on off-contract and on the
//                            joints/EP capitation opportunity
const ACCURACY_RATE = 0.2;
const CHARGE_CAPTURE_RATE = 0.05;
const PROCESSING_LABOR_RATE = 0.9;
const CONTRACT_PRICE_RATE = 0.05;
const ANNUAL_SUPPLY_GROWTH = 0.04;
const ANNUAL_LABOR_GROWTH = 0.05;
const SUBSCRIPTION_ESCALATOR = 0.05;

const formatCurrency = (value: number) => {
    const safe = Number.isFinite(value) ? value : 0;
    const abs = Math.abs(safe);
    const formatted = `$${abs.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
    return safe < 0 ? `-${formatted}` : formatted;
};

const displayNumber = (value: unknown): number => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
};

const safeNum = (value: unknown, fallback = 0): number => {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
};

const yearColumnSx = (colIndex: number) => ({
    borderLeft: colIndex > 0 ? "2px solid #e0e0e0" : undefined,
    backgroundColor: colIndex > 0 && colIndex % 2 === 0 ? "#f8f9fa" : undefined,
});

export const SameDayROICalculations = () => {
    const { dispatch } = useStore();
    const [roiTerm, setRoiTerm] = useState(5);
    const [chartView, setChartView] = useState<"annual" | "cumulative">("annual");

    const sameDay = useSelector((state: any) => state.inputFields.sameDay) ?? {};
    const spend = sameDay.spend ?? {};
    const investment = sameDay.investment ?? {};
    const numberOfHospitals = safeNum(sameDay.numberOfHospitals, 0);

    // ---- Inputs (percentages stored as 0..100 in the form, normalized here) ----
    const baseSpend = safeNum(spend[SAMEDAY_SPEND_KEYS.annualBillOnlySpend]);
    const pctNoErpId = safeNum(spend[SAMEDAY_SPEND_KEYS.pctNoErpId]) / 100;
    const pctPricingInaccuracyNoErp =
        safeNum(spend[SAMEDAY_SPEND_KEYS.pctPricingAccuracyNoErp]) / 100;
    const pctSpendOffContract =
        safeNum(spend[SAMEDAY_SPEND_KEYS.pctSpendOffContract]) / 100;
    const pctOffContractInaccuracy =
        safeNum(spend[SAMEDAY_SPEND_KEYS.pctOffContractPricingAccuracy]) / 100;
    const chargeCaptureAccuracy =
        safeNum(spend[SAMEDAY_SPEND_KEYS.pctChargeCaptureAccuracy]) / 100;
    const pctJoints = safeNum(spend[SAMEDAY_SPEND_KEYS.pctJoints]) / 100;
    const pctEp = safeNum(spend[SAMEDAY_SPEND_KEYS.pctEp]) / 100;
    const jointsCapitationAgreed =
        spend[SAMEDAY_SPEND_KEYS.jointsCapitationAgreed] === "yes";
    const epCapitationAgreed = spend[SAMEDAY_SPEND_KEYS.epCapitationAgreed] === "yes";
    const pctJointsCapitation = jointsCapitationAgreed
        ? safeNum(spend[SAMEDAY_SPEND_KEYS.pctJointsCapitation]) / 100
        : 0;
    const pctEpCapitation = epCapitationAgreed
        ? safeNum(spend[SAMEDAY_SPEND_KEYS.pctEpCapitation]) / 100
        : 0;

    const weeklyHoursTotal =
        safeNum(spend[SAMEDAY_SPEND_KEYS.weeklyHoursImplantCoordinators]) +
        safeNum(spend[SAMEDAY_SPEND_KEYS.weeklyHoursBuyers]) +
        safeNum(spend[SAMEDAY_SPEND_KEYS.weeklyHoursAccounting]);
    const fteWage = safeNum(spend[SAMEDAY_SPEND_KEYS.fteWage]);

    // ---- Investment ----
    // Legacy ROIs stored a monthly fee (annualized ×12); new ROIs store the annual
    // fee directly (×1). Read the cadence-specific key so both reopen correctly.
    const isLegacyRoi = useSelector((state: any) => !!state.inputFields.isLegacyRoi);
    const subscriptionPeriodsPerYear = isLegacyRoi ? 12 : 1;
    const subscriptionInput = safeNum(
        investment[
            isLegacyRoi
                ? SAMEDAY_INVESTMENT_KEYS.monthlySubscriptionAllHospitals
                : SAMEDAY_INVESTMENT_KEYS.annualSubscriptionAllHospitals
        ]
    );
    const implementationAll = safeNum(
        investment[SAMEDAY_INVESTMENT_KEYS.implementationAllHospitals]
    );
    const perHospitalFee = safeNum(
        investment[SAMEDAY_INVESTMENT_KEYS.perHospitalTrainingFee]
    );
    const interfaceFees = safeNum(investment[SAMEDAY_INVESTMENT_KEYS.interfaceFees]);

    const upfrontInvestment =
        implementationAll + perHospitalFee * numberOfHospitals + interfaceFees;

    // ---- Per-year arrays ----
    const spendByYear = useMemo(() => {
        const arr: number[] = [];
        for (let i = 0; i < roiTerm; i++) {
            arr.push(baseSpend * Math.pow(1 + ANNUAL_SUPPLY_GROWTH, i));
        }
        return arr;
    }, [baseSpend, roiTerm]);

    const accuracySavings = useMemo(
        () =>
            spendByYear.map((s) => {
                const uncontrolledSpend = (pctNoErpId + pctSpendOffContract) / 2;
                const pricingInaccuracy = (pctPricingInaccuracyNoErp + pctOffContractInaccuracy) / 2;
                return s * uncontrolledSpend * pricingInaccuracy * ACCURACY_RATE;
            }),
        [spendByYear, pctNoErpId, pctSpendOffContract, pctPricingInaccuracyNoErp, pctOffContractInaccuracy]
    );

    const chargeCaptureSavings = useMemo(() => {
        const uncaptured = Math.max(0, 1 - chargeCaptureAccuracy);
        return spendByYear.map((s) => s * uncaptured * CHARGE_CAPTURE_RATE);
    }, [spendByYear, chargeCaptureAccuracy]);

    const processingLaborSavings = useMemo(() => {
        const annualLaborYear1 = weeklyHoursTotal * 52 * fteWage;
        const year1 = annualLaborYear1 * PROCESSING_LABOR_RATE;
        const arr: number[] = [];
        for (let i = 0; i < roiTerm; i++) {
            arr.push(year1 * Math.pow(1 + ANNUAL_LABOR_GROWTH, i));
        }
        return arr;
    }, [weeklyHoursTotal, fteWage, roiTerm]);

    const contractPriceSavings = useMemo(() => {
        return spendByYear.map((s) => {
            const offContractDollars = s * pctSpendOffContract;
            const partA = offContractDollars * CONTRACT_PRICE_RATE;
            const jointsOpportunity =
                pctJoints * (1 - pctJointsCapitation);
            const epOpportunity = pctEp * (1 - pctEpCapitation);
            const partB = s * (jointsOpportunity + epOpportunity) * CONTRACT_PRICE_RATE;
            return partA + partB;
        });
    }, [
        spendByYear,
        pctSpendOffContract,
        pctJoints,
        pctEp,
        pctJointsCapitation,
        pctEpCapitation,
    ]);

    const annualSubscription = useMemo(() => {
        const baseAnnual = subscriptionInput * subscriptionPeriodsPerYear;
        const arr: number[] = [];
        for (let i = 0; i < roiTerm; i++) {
            arr.push(baseAnnual * Math.pow(1 + SUBSCRIPTION_ESCALATOR, i));
        }
        return arr;
    }, [subscriptionInput, subscriptionPeriodsPerYear, roiTerm]);

    const annualExpense = useMemo(
        () => annualSubscription.map((sub, i) => sub + (i === 0 ? upfrontInvestment : 0)),
        [annualSubscription, upfrontInvestment]
    );

    const savingsByYear = useMemo(
        () =>
            spendByYear.map(
                (_, i) =>
                    accuracySavings[i] +
                    chargeCaptureSavings[i] +
                    processingLaborSavings[i] +
                    contractPriceSavings[i]
            ),
        [
            spendByYear,
            accuracySavings,
            chargeCaptureSavings,
            processingLaborSavings,
            contractPriceSavings,
        ]
    );

    const annualRoi = useMemo(
        () => savingsByYear.map((s, i) => s - annualExpense[i]),
        [savingsByYear, annualExpense]
    );

    const termNetBenefit = useMemo(
        () => annualRoi.reduce((a, b) => a + b, 0),
        [annualRoi]
    );

    const paybackMonths = useMemo(() => {
        let cumulative = -upfrontInvestment;
        let months = 0;
        for (let i = 0; i < roiTerm; i++) {
            const monthly = (savingsByYear[i] - annualSubscription[i]) / 12;
            for (let j = 0; j < 12; j++) {
                cumulative += monthly;
                months++;
                if (cumulative >= 0) return months;
            }
        }
        return null;
    }, [savingsByYear, annualSubscription, upfrontInvestment, roiTerm]);

    const unifiedTable = useMemo(
        () => [
            {
                label: "Annual Investment in PAR SameDay",
                values: annualExpense,
                section: "investment",
            },
            {
                label: "Bill-Only Accuracy Improvement",
                values: accuracySavings,
                section: "savings",
            },
            {
                label: "Bill-Only Patient Charge Capture Improvement",
                values: chargeCaptureSavings,
                section: "savings",
            },
            {
                label: "Bill-Only Processing Labor Improvement",
                values: processingLaborSavings,
                section: "savings",
            },
            {
                label: "Bill-Only Contract Price Improvement",
                values: contractPriceSavings,
                section: "savings",
            },
            { label: "Annual ROI", values: annualRoi, section: "roi" },
            {
                label: "Annual ROI %",
                values: annualRoi.map((roi, i) =>
                    annualExpense[i] > 0 ? (roi / annualExpense[i]) * 100 : 0
                ),
                section: "roiPct",
            },
        ],
        [
            annualExpense,
            accuracySavings,
            chargeCaptureSavings,
            processingLaborSavings,
            contractPriceSavings,
            annualRoi,
        ]
    );

    // Persist totals so save/load + downstream views still work.
    useEffect(() => {
        const savingsTable = [
            ["Bill-Only Accuracy Improvement", ...accuracySavings],
            ["Bill-Only Patient Charge Capture Improvement", ...chargeCaptureSavings],
            ["Bill-Only Processing Labor Improvement", ...processingLaborSavings],
            ["Bill-Only Contract Price Improvement", ...contractPriceSavings],
        ];
        const savingsTotals = ["Annual Savings Total", ...savingsByYear];
        const investmentTable = [["Annual Investment in PAR SameDay", ...annualExpense]];
        const annualRoiTotals = ["Annual ROI (Savings - Costs)", ...annualRoi];

        dispatch(setTotals({ name: "savingsTable", value: savingsTable }));
        dispatch(setTotals({ name: "savingsTotals", value: savingsTotals }));
        dispatch(setTotals({ name: "investmentTable", value: investmentTable }));
        dispatch(setTotals({ name: "annualRoiTotals", value: annualRoiTotals }));
        dispatch(setTotals({ name: "termNetBenefit", value: termNetBenefit }));
        dispatch(setTotals({ name: "paybackMonths", value: paybackMonths ?? 0 }));
        dispatch(setTotals({ name: "isSameDayOnly", value: true }));
        dispatch(setTotals({ name: "sameDayUpfront", value: upfrontInvestment }));
    }, [
        dispatch,
        accuracySavings,
        chargeCaptureSavings,
        processingLaborSavings,
        contractPriceSavings,
        savingsByYear,
        annualExpense,
        annualRoi,
        termNetBenefit,
        paybackMonths,
        upfrontInvestment,
    ]);

    const barLabels = Array.from({ length: roiTerm }, (_, i) => `Year ${i + 1}`);

    const annualChartData = {
        labels: barLabels,
        datasets: [
            {
                label: "Annual Net Savings",
                data: annualRoi,
                backgroundColor: annualRoi.map((v) =>
                    v >= 0 ? "rgba(16, 185, 129, 0.85)" : "rgba(239, 68, 68, 0.8)"
                ),
                borderColor: annualRoi.map((v) => (v >= 0 ? "#059669" : "#DC2626")),
                borderWidth: 1,
                borderRadius: 4,
            },
            {
                label: "Annual Investment",
                data: annualExpense.map((v) => Math.abs(v)),
                backgroundColor: "rgba(99, 102, 241, 0.75)",
                borderColor: "#4F46E5",
                borderWidth: 1,
                borderRadius: 4,
            },
        ],
    };

    const cumulativeChartData = {
        labels: [...barLabels, "Total"],
        datasets: [
            {
                label: "Net Savings",
                data: [...annualRoi, termNetBenefit],
                backgroundColor: [
                    ...annualRoi.map((v) =>
                        v >= 0 ? "rgba(59, 130, 246, 0.8)" : "rgba(239, 68, 68, 0.8)"
                    ),
                    "rgba(245, 158, 11, 0.85)",
                ],
                borderColor: [
                    ...annualRoi.map((v) => (v >= 0 ? "#2563EB" : "#DC2626")),
                    "#D97706",
                ],
                borderWidth: 1,
                borderRadius: 4,
            },
        ],
    };

    const chartOptions = {
        responsive: true,
        plugins: {
            legend: { display: chartView === "annual" },
            title: {
                display: true,
                text:
                    chartView === "annual"
                        ? "Annual Net Savings vs. Investment"
                        : "Net Savings by Year",
                font: { size: 16, weight: "bold" as const },
                padding: { bottom: 20 },
            },
            tooltip: {
                callbacks: {
                    label: (context: any) => {
                        const value = context.parsed.y ?? 0;
                        const formatted = formatCurrency(value);
                        const prefix = context.dataset.label
                            ? `${context.dataset.label}: `
                            : "";
                        return `${prefix}${formatted}`;
                    },
                },
            },
        },
        scales: {
            y: {
                ticks: { callback: (value: any) => formatCurrency(Number(value)) },
            },
            x: { grid: { display: false } },
        },
    };

    const handleTermChange = (_e: MouseEvent<HTMLElement>, term: number) => {
        if (term != null) setRoiTerm(term);
    };

    const handleChartViewChange = (
        _e: MouseEvent<HTMLElement>,
        next: "annual" | "cumulative"
    ) => {
        if (next != null) setChartView(next);
    };

    return (
        <Slide direction="right" in={true} mountOnEnter unmountOnExit>
            <div className="sameday-roi-calculations-page" style={{ textAlign: "center" }}>
                <h1>PAR SameDay ROI Calculations</h1>

                <div style={{ marginBottom: "30px" }}>
                    <div
                        style={{
                            marginBottom: "15px",
                            fontSize: "15px",
                            fontWeight: 600,
                        }}
                    >
                        ROI Analysis Term:
                    </div>
                    <ToggleButtonGroup
                        color="primary"
                        value={roiTerm}
                        exclusive
                        onChange={handleTermChange}
                    >
                        <ToggleButton value={5}>5 Years</ToggleButton>
                        <ToggleButton value={7}>7 Years</ToggleButton>
                        <ToggleButton value={10}>10 Years</ToggleButton>
                    </ToggleButtonGroup>
                </div>

                <h3>Annual Impact &amp; ROI</h3>
                <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 650 }} size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell style={{ fontWeight: 600, minWidth: 280 }}>
                                    {" "}
                                </TableCell>
                                {Array.from({ length: roiTerm }, (_, index) => (
                                    <TableCell
                                        key={index}
                                        style={{
                                            fontWeight: 700,
                                            fontSize: "0.95rem",
                                        }}
                                        align="right"
                                        sx={yearColumnSx(index + 1)}
                                    >
                                        Year {index + 1}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {unifiedTable.map((row) => {
                                const isInvestment = row.section === "investment";
                                const isRoi = row.section === "roi";
                                const isRoiPct = row.section === "roiPct";

                                return (
                                    <TableRow
                                        key={row.label}
                                        hover={!(isRoi || isRoiPct)}
                                        sx={{
                                            ...((isRoi || isRoiPct) && {
                                                "& td": {
                                                    fontWeight: 700,
                                                    fontSize: "0.95rem",
                                                },
                                            }),
                                        }}
                                    >
                                        <TableCell
                                            sx={{
                                                fontWeight:
                                                    isInvestment || isRoi || isRoiPct ? 700 : 400,
                                                ...(isInvestment && { fontStyle: "italic" }),
                                            }}
                                        >
                                            {row.label}
                                        </TableCell>
                                        {row.values.map((val, colIdx) => (
                                            <TableCell
                                                key={colIdx}
                                                align="right"
                                                sx={{
                                                    ...yearColumnSx(colIdx + 1),
                                                    fontWeight:
                                                        isInvestment || isRoi || isRoiPct
                                                            ? 700
                                                            : 400,
                                                    ...(isInvestment && {
                                                        backgroundColor: "#e3f2fd",
                                                    }),
                                                    ...((isRoi || isRoiPct) && {
                                                        backgroundColor: "#e8f5e9",
                                                    }),
                                                }}
                                            >
                                                {isRoiPct ? (
                                                    `${displayNumber(val).toFixed(0)}%`
                                                ) : (
                                                    <NumberFormat
                                                        displayType="text"
                                                        thousandSeparator={","}
                                                        prefix={isInvestment ? "($" : "$"}
                                                        suffix={isInvestment ? ")" : ""}
                                                        decimalScale={0}
                                                        type="text"
                                                        value={Math.abs(displayNumber(val))}
                                                    />
                                                )}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

                <div style={{ width: "70%", margin: "30px auto 0" }}>
                    <ToggleButtonGroup
                        color="primary"
                        value={chartView}
                        exclusive
                        onChange={handleChartViewChange}
                        size="small"
                        sx={{ marginBottom: "20px" }}
                    >
                        <ToggleButton value="annual">Annual Net Savings</ToggleButton>
                        <ToggleButton value="cumulative">Cumulative</ToggleButton>
                    </ToggleButtonGroup>
                    <Bar
                        options={chartOptions}
                        data={chartView === "annual" ? annualChartData : cumulativeChartData}
                    />
                </div>

                <div
                    style={{
                        marginTop: "30px",
                        fontSize: "0.85rem",
                        color: "#666",
                        textAlign: "left",
                        width: "70%",
                        margin: "30px auto 0",
                    }}
                >
                    <p>
                        Bill-only spend grows at {Math.round(ANNUAL_SUPPLY_GROWTH * 100)}% per
                        year and labor at {Math.round(ANNUAL_LABOR_GROWTH * 100)}% per year.
                        Year 1 bill-only spend:{" "}
                        <NumberFormat
                            displayType="text"
                            thousandSeparator={","}
                            prefix="$"
                            decimalScale={0}
                            type="text"
                            value={baseSpend}
                        />
                        .
                        {numberOfHospitals > 0 && (
                            <> Deployment covers {numberOfHospitals} hospital
                                {numberOfHospitals !== 1 ? "s" : ""}.</>
                        )}
                    </p>
                    <p>
                        Payback:{" "}
                        {paybackMonths == null
                            ? `not reached within ${roiTerm} years`
                            : `${paybackMonths} months`}
                        . Term net benefit:{" "}
                        <NumberFormat
                            displayType="text"
                            thousandSeparator={","}
                            prefix="$"
                            decimalScale={0}
                            type="text"
                            value={termNetBenefit}
                        />
                        .
                    </p>
                </div>
            </div>
        </Slide>
    );
};
